import memoize from 'memoize-one';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { courseCheckoutStepViewed, coursePurchased } from 'my-core/gtm-events';
import { useCurrentUser, useStandardApiRequest } from 'my-core/hooks';
import { useFetchSubscription } from 'my-core/subscription-utils';

import CoursePurchaseCheckout from './CoursePurchaseCheckout';

import { purchaseSubscription } from 'my-actions/SubscriptionActions';
import { confirmCoursePurchase } from 'my-actions/UserCourseActions';

export default function CoursePurchaseCheckoutCtnr({
  courseId,
  defaultDiscountCode,
  defaultPlan,
  noDialog,
  onClose,
  open,
}) {
  const currentUser = useCurrentUser();
  const [activeStep, setActiveStep] = useState(0);

  const course = useSelector(state => state.courses.items[courseId]);
  const product = useSelector(state => state.stripeProducts.items[course.stripe_product_id]);

  const userCourse = useSelector(state => getUserCourse(course?.id, currentUser.id, state.userCourses.items));

  const [subscription /*, fetchSubscriptionStatus */] = useFetchSubscription(course.stripe_product_id);
  const [paymentIntent, discountCode] =
    ['canceled', 'incomplete_expired'].includes(subscription?.status) ?
      []
    : [subscription?.payment_intent, subscription?.discount_code];
  const existingPaymentMethod = subscription?.payment_method;

  const { performRequest: performConfirmPurchase, requestStatus: confirmPurchaseStatus } = useStandardApiRequest({
    actionCreator: confirmCoursePurchase,
    errorMessage: true,
    // onSuccess: () => setActiveStep(1)
  });

  const { performRequest: performSubscriptionPurchase, requestStatus: purchaseSubscriptionStatus } =
    useStandardApiRequest({
      actionCreator: purchaseSubscription,
    });

  const purchaseDetails = useRef();
  const handlePayment = useCallback(
    ({
      callback,
      currency,
      discountCode,
      intent,
      paymentIntent,
      paymentMethod,
      plan,
      purchaseOpts,
      subscriptionId,
      total,
    }) => {
      purchaseDetails.current = { total, currency, discountCode, callback };
      if (course.stripe_product_id) {
        performSubscriptionPurchase({
          applyCredits: true,
          discountCodeId: discountCode?.id,
          extraOptions: purchaseOpts,
          intent,
          interval: plan,
          productId: course.stripe_product_id,
          paymentMethod: paymentIntent?.payment_method || paymentMethod,
          subscriptionId,
        });
      } else {
        performConfirmPurchase(course?.id, paymentIntent?.id, purchaseOpts, discountCode?.id);
      }
      // alert(`DONE: ${JSON.stringify(payment)}`);
    },
    [performConfirmPurchase, performSubscriptionPurchase, course?.id, course?.stripe_product_id],
  );

  // don't use performConfirmPurchase#onSuccess option here because update may be triggered by ws push update (ie. partial.ly payment)
  useEffect(() => {
    if (['active', 'pending_payment'].includes(userCourse?.status) || subscription?.status === 'active') {
      setActiveStep(1);
    }
  }, [userCourse?.status, subscription?.status]);

  // Tracking Events
  useEffect(() => {
    if ((open || noDialog) && course) {
      const step = activeStep === 0 ? 'payment' : 'confirmation';
      courseCheckoutStepViewed(course, step);
      if (step === 'confirmation') {
        coursePurchased(course, userCourse, subscription, purchaseDetails.current);
        purchaseDetails.current?.callback?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noDialog, open, course?.id, activeStep]);

  const purchaseError =
    confirmPurchaseStatus?.error ||
    purchaseSubscriptionStatus?.error ||
    (purchaseSubscriptionStatus && !purchaseSubscriptionStatus.fetching && paymentIntent?.last_payment_error?.message);

  return (
    <CoursePurchaseCheckout
      activeStep={activeStep}
      confirming={!!(confirmPurchaseStatus?.fetching || purchaseSubscriptionStatus?.fetching)}
      course={course}
      currentUser={currentUser}
      defaultDiscountCode={defaultDiscountCode}
      defaultPlan={defaultPlan}
      discountCode={discountCode}
      noDialog={noDialog}
      onChangeStep={setActiveStep}
      onClose={onClose}
      onPaymentSuccess={handlePayment}
      open={open}
      paymentIntent={paymentIntent}
      paymentMethod={existingPaymentMethod}
      product={product}
      purchaseError={purchaseError}
      subscription={subscription}
      userCourse={userCourse}
    />
  );
}

const getUserCourse = memoize((courseId, userId, userCourses) =>
  Object.values(userCourses).find(uc => uc.user_id === userId && uc.course_id === courseId),
);
